import { FunctionComponent, useEffect } from "react";

import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { getOrderedProductPermissions, orderedActionsPermissions } from "@simplyk/common";

import { useCurrentUserContext } from "../../../../contexts/CurrentUserContext";
import { SMSCodeStep } from "../../../SMSCodeStep/SMSCodeStep";
import { useResendCode } from "../../../SMSCodeStep/useResendCode";

import { UserManagementModalProps, useUserManagementModal } from "./useUserManagementModal";

import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardCheckboxGroup } from "@/dashboard-design-system/DashboardCheckboxGroup/DashboardCheckboxGroup";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardEmailTextField } from "@/dashboard-design-system/EmailTextField/DashboardEmailTextField";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { isNameValid, isRequired } from "@/helpers/validators";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { AddUser as UserAddIcon, Lock as LockIcon } from "@/icons/outlined";

export const UserManagementModal: FunctionComponent<React.PropsWithChildren<UserManagementModalProps>> = (props) => {
  const { t } = useTranslate();
  const { isPhoneScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const { organization } = useCurrentUserContext();

  const {
    isEditUser,
    isUserOwner,
    onSubmit,
    form,
    submitIsLoading,
    handlePermissionChange,
    getPermissionCheckboxLabel,
    step,
    code,
    setCode,
    maskedPhoneNumber,
  } = useUserManagementModal(props);

  const {
    isResendButtonDisabled,
    resendButtonCountdownText,
    resendCode,
    isLoadingSendSms: isLoadingResendSms,
  } = useResendCode({});

  const { errors } = form.formState;

  const productCheckboxes = getPermissionCheckboxLabel(getOrderedProductPermissions(organization?.country));
  const actionsCheckboxes = getPermissionCheckboxLabel(orderedActionsPermissions);
  const isDisabledSubmitCodeButton = !code || code.length !== 6;

  useEffect(() => {
    if (step === "SMS_CODE_VERIFY") {
      logAmplitudeEvent(AmplitudeEvents.TwoFAPrompted, {
        moment: isEditUser ? "bank rights added to user" : "user with bank rights invited",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <DashboardDialog
      data-test="user-management-modal"
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      fullScreenOnMobile
      maxWidth="sm"
      icon={step === "UPDATE_USERS" ? <UserAddIcon /> : <LockIcon />}
      hideIcon={isPhoneScreen}
      sx={{
        zIndex: 11000, // Higher than z-index of the share form modal on the form page(10000)
      }}
      title={
        step === "UPDATE_USERS"
          ? props.title || t("dashboard", "usersManagement.addUser")
          : t("dashboard", "usersManagement.table.mfaStep.title")
      }
      actions={{
        main: {
          onClick: onSubmit,
          isLoading: submitIsLoading,
          disabled: isUserOwner || (step === "SMS_CODE_VERIFY" && isDisabledSubmitCodeButton),
          children: t("dashboard", isEditUser ? "usersManagement.save" : "usersManagement.sendInvitation"),
          ["data-test"]: "submit-button",
          hidden: step === "SMS_CODE_VERIFY" && !isPhoneScreen,
        },
        cancel: {
          onClick: props.onClose,
          children: t("dashboard", "cancel"),
          ["data-test"]: "cancel",
          hidden: step === "SMS_CODE_VERIFY" && !isPhoneScreen,
        },
      }}
    >
      <form noValidate>
        <Grid container spacing={2} sx={{ display: step === "UPDATE_USERS" ? "flex" : "none" }}>
          <Grid item xs={6}>
            <DashboardTextField
              control={form.control}
              size="medium"
              name="firstName"
              data-test="first-name"
              label={t("dashboard", "usersManagement.firstName")}
              disabled={isEditUser}
              errorMessage={errors["firstName"]?.message}
              rules={[
                isRequired(t("dashboard", "usersManagement.firstNameIsRequired")),
                isNameValid(t("common", "invalidCharacterUsed")),
              ]}
            />
          </Grid>
          <Grid item xs={6}>
            <DashboardTextField
              control={form.control}
              size="medium"
              name="lastName"
              data-test="last-name"
              disabled={isEditUser}
              label={t("dashboard", "usersManagement.lastName")}
              errorMessage={errors["lastName"]?.message}
              rules={[
                isRequired(t("dashboard", "usersManagement.lastNameIsRequired")),
                isNameValid(t("common", "invalidCharacterUsed")),
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <DashboardEmailTextField
              control={form.control}
              size="medium"
              name="email"
              data-test="email"
              disabled={isEditUser}
              label={t("dashboard", "usersManagement.email")}
              errorMessage={errors["email"]?.message}
              rules={[
                {
                  required: {
                    value: true,
                    message: t("dashboard", "usersManagement.emailIsRequired") as string,
                  },
                },
              ]}
            />
          </Grid>
          {productCheckboxes && actionsCheckboxes && (
            <Grid item xs={12}>
              <Grid item container>
                <Grid item xs={12}>
                  <DashboardTypography variant="subtitle1">
                    {t("dashboard", "usersManagement.permissions")}
                  </DashboardTypography>
                </Grid>
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={12}>
                      <DashboardCheckboxGroup
                        checkboxes={productCheckboxes}
                        label={t("dashboard", "usersManagement.permissions.productAccess")}
                        onChange={handlePermissionChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container>
                    <Grid item xs={12}>
                      <DashboardCheckboxGroup
                        checkboxes={actionsCheckboxes}
                        label={t("dashboard", "usersManagement.permissions.actions")}
                        onChange={handlePermissionChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Box sx={{ display: step === "SMS_CODE_VERIFY" ? "block" : "none" }}>
          <Grid container spacing={1}>
            <Grid item>
              <SMSCodeStep
                description={
                  <DashboardTypography variant="body2">
                    {t("dashboard", "usersManagement.permissions.edit.mfaDescription", {
                      maskedPhone: maskedPhoneNumber,
                    })}
                  </DashboardTypography>
                }
                code={code}
                setCode={setCode}
                submitButtonText={t("common", "Submit")}
                resendButtonText={resendButtonCountdownText}
                isDisabledSubmitCodeButton={isDisabledSubmitCodeButton}
                isLoadingResendSms={isLoadingResendSms}
                isResendButtonDisabled={isResendButtonDisabled}
                handleResendCode={resendCode}
                isLoadingSubmitCode={submitIsLoading}
                onSubmitCode={onSubmit}
              />
            </Grid>
          </Grid>
        </Box>
      </form>
    </DashboardDialog>
  );
};
